import { Input } from "components/Form/Input";
import { Select } from "components/Form/Select";
import { OtherLabel } from "contexts/CollectionDetails";
import { useCollectionDetailsForm } from "../../contexts/CollectionDetails/useCollectionDetailsForm";
import { getUserEmail } from "lib/get-user-email";

export function GalleryPreviewLabels({
  readOnly = false,
}: {
  readOnly?: boolean;
}) {
  const { formState, labels, submit } = useCollectionDetailsForm();

  const watchDevice = formState.watch("device");
  const watchLabels = formState.watch("labels");
  const watchData = formState.watch("data");

  return (
    <div className="grid gap-4">
      <h3 className="text-858494 font-medium">Signed in as</h3>
      <span className="bg-f3f3f4 border-f4f7ff px-2.5 py-4 text-374151 rounded-xl">
        {getUserEmail()}
      </span>
      <div className="bg-white border border-[#E5E7EB] rounded-xl">
        <div className="grid gap-4 px-2.5 py-4">
          <h3 className="text-858494 font-medium">Selected labels</h3>
          {labels && (
            <form className="grid gap-2.5" onSubmit={(e) => e.preventDefault()}>
              {labels.devices &&
                labels.devices.length > 0 &&
                (readOnly ? (
                  <div className="text-sm text-858494">
                    <span className="font-medium">Device:</span>{" "}
                    {watchDevice || "Not selected"}
                  </div>
                ) : (
                  <Select
                    placeholder="Select label"
                    options={Object.fromEntries(
                      Object.entries(labels.devices).map(
                        ([, { key, label }]) => [label, key]
                      )
                    )}
                    value={watchDevice}
                    onChange={(v) => {
                      formState.clearErrors("device");
                      formState.setValue("device", v as string, {
                        shouldValidate: true,
                      });
                      const camera_not_flipped = labels.devices.find(
                        (i) => i.key === v
                      )?.camera_not_flipped;

                      if (camera_not_flipped !== undefined) {
                        formState.setValue(
                          "camera_not_flipped",
                          camera_not_flipped
                        );
                      }
                      submit();
                    }}
                    renderValue={(value) =>
                      labels.devices.find((device) => device.key === value)
                        ?.label ?? value
                    }
                    renderListItem={({ value, label }) => `${label}:${value}`}
                  />
                ))}

              {Object.entries(labels)
                .filter(
                  (i): i is [string, OtherLabel] =>
                    "title" in i[1] && !!i[1].field_type
                )
                .map(([id, field]) => (
                  <div key={id}>
                    {readOnly ? (
                      <div className="text-sm text-858494">
                        <span className="font-medium">{field.title}:</span>{" "}
                        {watchLabels?.[id] || "Not set"}
                      </div>
                    ) : (
                      <>
                        {/* Task-level field types only */}
                        {field.field_type === "text" && (
                          <Input
                            placeholder={`Enter ${field.title.toLowerCase()}`}
                            value={(watchLabels?.[id] as string) || ""}
                            onChange={(e) => {
                              formState.setValue(
                                `labels.${id}`,
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              );
                              formState.clearErrors(`labels.${id}`);
                              submit();
                            }}
                          />
                        )}

                        {field.field_type === "numeric" && (
                          <Input
                            type="number"
                            placeholder={`Enter ${field.title.toLowerCase()}`}
                            value={(watchLabels?.[id] as string) || ""}
                            onChange={(e) => {
                              formState.setValue(
                                `labels.${id}`,
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              );
                              formState.clearErrors(`labels.${id}`);
                              submit();
                            }}
                          />
                        )}

                        {field.field_type === "date" && (
                          <Input
                            type="date"
                            value={(watchLabels?.[id] as string) || ""}
                            onChange={(e) => {
                              formState.setValue(
                                `labels.${id}`,
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              );
                              formState.clearErrors(`labels.${id}`);
                              submit();
                            }}
                          />
                        )}

                        {field.field_type === "dropdown" && (
                          <Select
                            options={field.labels}
                            value={watchLabels?.[id]}
                            onChange={(v) => {
                              formState.clearErrors(`labels.${id}`);
                              formState.setValue(`labels.${id}`, v, {
                                shouldValidate: true,
                                shouldTouch: true,
                              });
                              submit();
                            }}
                            renderValue={(i) =>
                              [i]
                                .flat()
                                .map(
                                  (value) =>
                                    Object.entries(field.labels).find(
                                      ([, storedValue]) => storedValue === value
                                    )?.[0] ?? value
                                )
                                .join(", ")
                            }
                            renderListItem={(i) => `${i.label}:${i.value}`}
                            listClassName="max-h-[40vh] overflow-y-auto"
                            variant="white"
                          />
                        )}
                      </>
                    )}
                  </div>
                ))}

              {watchData?.map?.((value, i) =>
                readOnly ? (
                  <div key={i} className="text-sm text-858494">
                    <span className="font-medium">
                      Additional info {i + 1}:
                    </span>{" "}
                    {value || "Not set"}
                  </div>
                ) : (
                  <Input
                    key={i}
                    {...formState.register(`data.${i}`)}
                    onChange={(v) => {
                      formState.setValue(`data.${i}`, v.currentTarget.value, {
                        shouldValidate: true,
                      });

                      submit();
                    }}
                  />
                )
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
